import type { ActionDefinition, ActionType } from '../types';
import type { DOMAdapter, TimerAdapter, AnimationAdapter, RippleAdapter } from '../types';

/**
 * Context passed to action handlers
 */
export interface ActionContext {
  domAdapter: DOMAdapter;
  timerAdapter: TimerAdapter;
  animationAdapter: AnimationAdapter;
  rippleAdapter: RippleAdapter;
}

/**
 * Handler for a specific action type
 */
export interface ActionHandler<T extends ActionDefinition = ActionDefinition> {
  /**
   * Execute the action
   */
  execute(action: T, context: ActionContext): Promise<void>;

  /**
   * Validate the action configuration (optional)
   */
  validate?(action: T): boolean;
}

/**
 * Registry of action handlers
 * Follows the plugin pattern for extensibility
 */
export class ActionRegistry {
  private handlers = new Map<ActionType, ActionHandler>();

  /**
   * Register a handler for an action type
   */
  register(type: ActionType, handler: ActionHandler): void {
    if (this.handlers.has(type)) {
      console.warn(`Overwriting existing handler for action type: ${type}`);
    }
    this.handlers.set(type, handler);
  }

  /**
   * Unregister a handler
   */
  unregister(type: ActionType): void {
    this.handlers.delete(type);
  }

  /**
   * Get handler for action type
   */
  get(type: ActionType): ActionHandler | undefined {
    return this.handlers.get(type);
  }

  /**
   * Check if handler exists
   */
  has(type: ActionType): boolean {
    return this.handlers.has(type);
  }

  /**
   * Get all registered action types
   */
  getRegisteredTypes(): ActionType[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear();
  }
}
